import React from 'react';
import {
  ShoppingCart, Eye, Calendar, Clock, Edit2, CheckSquare, Square, Copy, FileText,
} from 'lucide-react';
import { Card, CardContent } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../../components/ui/select';
import { statusConfig, safeDate, NON_EDITABLE_STATUSES, GATED_STATUS, SETTABLE_STATUS_ENTRIES } from './ordersConstants';
import { LocationLine } from '../../../components/LocationLine';

export const OrdersTable = ({
  orders,
  selectedOrderIds,
  onToggleSelect,
  onToggleSelectAll,
  onView,
  onReorder,
  onEditRow,
  onStatusChange,
  onReviewApproval,
}) => (
  <Card>
    <CardContent className="p-0">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50 border-b">
            <tr>
              <th className="p-4 w-10">
                <button onClick={onToggleSelectAll} className="flex items-center justify-center" data-testid="select-all-orders">
                  {selectedOrderIds.size === orders.length && orders.length > 0 ? (
                    <CheckSquare className="h-5 w-5 text-amber-500" />
                  ) : (
                    <Square className="h-5 w-5 text-gray-400" />
                  )}
                </button>
              </th>
              <th className="text-left p-4 text-xs font-medium text-gray-500 uppercase">Order</th>
              <th className="text-left p-4 text-xs font-medium text-gray-500 uppercase">Client</th>
              <th className="text-left p-4 text-xs font-medium text-gray-500 uppercase">Delivery</th>
              <th className="text-left p-4 text-xs font-medium text-gray-500 uppercase">Status</th>
              <th className="text-right p-4 text-xs font-medium text-gray-500 uppercase">Amount</th>
              <th className="text-right p-4 text-xs font-medium text-gray-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {orders.map((order) => {
              const status = statusConfig[order.status] || { label: 'Unknown', color: 'bg-gray-100 text-gray-800', icon: FileText };
              const isSelected = selectedOrderIds.has(order.id);
              return (
                <tr key={order.id} className={`hover:bg-gray-50 ${isSelected ? 'bg-amber-50' : ''}`}>
                  <td className="p-4">
                    <button onClick={() => onToggleSelect(order.id)} className="flex items-center justify-center" data-testid={`select-order-${order.id}`}>
                      {isSelected ? (
                        <CheckSquare className="h-5 w-5 text-amber-500" />
                      ) : (
                        <Square className="h-5 w-5 text-gray-400" />
                      )}
                    </button>
                  </td>
                  <td className="p-4">
                    <p className="font-medium text-gray-900">
                      {order.order_number || 'N/A'}
                      {order.is_custom && (
                        <span className="ml-1.5 text-xs bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded-full font-normal">Custom</span>
                      )}
                      {order.source === 'recurring' && (
                        <span className="ml-1.5 text-xs bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-full font-normal" data-testid={`recurring-badge-${order.id}`}>Recurring</span>
                      )}
                      {order.billing_timing === 'advance' && (
                        <span className="ml-1.5 text-xs bg-slate-200 text-slate-700 px-1.5 py-0.5 rounded-full font-normal" data-testid={`advance-badge-${order.id}`}>Advance</span>
                      )}
                      {order.delivery_proof_sent_count > 0 && (
                        <span
                          className="ml-1.5 text-xs bg-green-100 text-green-700 px-1.5 py-0.5 rounded-full font-normal"
                          title="Proof of delivery has already been sent to this client"
                          data-testid={`proof-sent-badge-${order.id}`}
                        >
                          Proof sent
                        </span>
                      )}
                    </p>
                    <p className="text-xs text-gray-500">
                      {order.created_at ? new Date(order.created_at).toLocaleDateString() : 'N/A'}
                    </p>
                  </td>
                  <td className="p-4">
                    <p className="text-gray-900">{order.client_name || 'Unknown'}</p>
                    <LocationLine
                      name={order.location_name}
                      source={order.location_name_source}
                      clientName={order.client_name}
                      testId={`order-row-location-${order.id}`}
                    />
                  </td>
                  <td className="p-4">
                    <div className="flex items-center gap-1 text-gray-600">
                      <Calendar className="h-3 w-3" />
                      <span className="text-sm">
                        {order.delivery_date
                          ? safeDate(order.delivery_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
                          : 'N/A'}
                      </span>
                    </div>
                    <div className="flex items-center gap-1 text-gray-500 text-xs">
                      <Clock className="h-3 w-3" />
                      {order.delivery_time_window || 'N/A'}
                    </div>
                  </td>
                  <td className="p-4">
                    {order.status === GATED_STATUS ? (
                      <div data-testid={`held-cell-${order.id}`}>
                        <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800">
                          <Clock className="h-3 w-3" />
                          Pending approval
                        </span>
                        {order.approval_deadline && (
                          <p className="text-xs text-gray-500 mt-1">
                            Decide by {safeDate(order.approval_deadline)?.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                          </p>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 px-2 mt-1 text-xs text-orange-700 hover:text-orange-800 hover:bg-orange-50"
                          onClick={() => onReviewApproval?.(order)}
                          data-testid={`review-held-${order.id}`}
                        >
                          Review {order.over_cap_line_count || 0} line{order.over_cap_line_count === 1 ? '' : 's'}
                        </Button>
                      </div>
                    ) : (
                      <Select
                        value={order.status || 'submitted'}
                        onValueChange={(value) => onStatusChange(order.id, value)}
                      >
                        <SelectTrigger className={`w-40 h-8 text-xs ${status.color}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {SETTABLE_STATUS_ENTRIES.map(([key, config]) => (
                            <SelectItem key={key} value={key}>{config.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  </td>
                  <td className="p-4 text-right">
                    <p className="font-semibold text-gray-900">${(order.total_amount || 0).toFixed(2)}</p>
                    <p className="text-xs text-gray-500">{order.items?.length || 0} items</p>
                  </td>
                  <td className="p-4 text-right">
                    <div className="flex gap-1 justify-end">
                      <Button variant="ghost" size="sm" onClick={() => onView(order)} data-testid={`view-order-${order.id}`}>
                        <Eye className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        title="Quick re-order"
                        data-testid={`reorder-row-${order.id}`}
                        onClick={() => onReorder(order)}
                      >
                        <Copy className="h-4 w-4" />
                      </Button>
                      {!NON_EDITABLE_STATUSES.has(order.status) && (
                        <Button
                          variant="ghost"
                          size="sm"
                          data-testid={`edit-order-row-${order.id}`}
                          onClick={() => onEditRow(order)}
                        >
                          <Edit2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {orders.length === 0 && (
        <div className="text-center py-12">
          <ShoppingCart className="h-12 w-12 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500">No orders found</p>
        </div>
      )}
    </CardContent>
  </Card>
);
